
import { useParams, useNavigate } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { TeamApplicationHeader } from "./components/TeamApplicationHeader";
import { ApplicationTabs } from "./components/ApplicationTabs";
import { useTeams } from "./hooks/useTeams";
import { useApplications } from "./hooks/useApplications";
import { useLanguage } from "@/contexts/LanguageContext";

const TeamApplication = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();

  const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(teamId || '');

  const { data: teams } = useTeams();
  const { data: applications, isLoading, refetch } = useApplications({ teamId, isValidUUID });

  const handleAddApplication = async (title: string, link: string, status: string) => {
    if (!title || !link) {
      toast({
        title: t("missingInformation"),
        description: t("pleaseProvidetitleAndLink"),
        variant: "destructive",
      });
      return;
    }

    if (!isValidUUID) {
      toast({
        title: t("invalidTeamId"),
        description: t("pleaseSelectTeamFirst"),
        variant: "destructive",
      });
      return;
    }

    const { error } = await supabase
      .from('applications')
      .insert([
        {
          title: title,
          content: link,
          team_id: teamId,
          status: status,
        },
      ]);

    if (error) {
      toast({
        title: t("errorCreatingApplication"),
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: t("success"),
      description: t("applicationCreatedSuccessfully"),
    });

    refetch();
  };

  const handleTeamChange = (selectedTeamId: string) => {
    navigate(`/applications/team/${selectedTeamId}`);
  };

  return (
    <AppLayout>
      <div className="space-y-8 animate-fadeIn">
        <TeamApplicationHeader
          teamId={teamId}
          teams={teams}
          onTeamChange={handleTeamChange}
          onAddApplication={handleAddApplication}
        />
        <ApplicationTabs
          applications={applications || []}
          isLoading={isLoading}
        />
      </div>
    </AppLayout>
  );
};

export default TeamApplication;
